import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import cn from '../className.js'
import Icon from '../icons/Icon.js'

export const MOVE_BEFORE = 'MOVE_BEFORE'
export const MOVE_AFTER = 'MOVE_AFTER'

export const DragAndDropContext = createContext()
export const DraggableContext = createContext()

export function useDragAndDropContext() {
  return useContext(DragAndDropContext)
}

export function useDraggableContext() {
  return useContext(DraggableContext)
}

function defaultOnDragStart(id) {
  return {
    values: [id],
    dragElementFunc() {
      return null
    },
  }
}

function defaultOnDragEnd() {
  return
}

/**
 * A draggable list that must use Draggable items with DragHandle
 *
 * @param {Object} props
 * @param {Function} props.onMove
 * @param {Function} props.onDragStart
 * @param {Function} props.onDragEnd
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function DragAndDropList({onMove, onDragStart, onDragEnd, children}) {
  const [drag, setDrag] = useState(null)
  onDragStart ||= defaultOnDragStart
  onDragEnd ||= defaultOnDragEnd

  const onMoveInternal = useCallback((id, nearID, where) => {
    if (onMove) {
      onMove(id, nearID, where)
    }
  }, [])

  // We don't get an "ending" event for some drags.
  // Dragging between two column in EditTopNavModal
  // and then to the bottom causes the issue,
  // which leaves the blank drag over state hanging around
  useEffect(() => {
    // Only setup when we get a drag object setup
    if (!drag) {
      return
    }

    // listen for pointer move events
    const listener = (event) => {
      // if no buttons are pressed
      if (event.buttons === 0) {
        cleanupDrag(setDrag)
      }
    }
    document.addEventListener('pointermove', listener)

    // normal drag finished remove listener as well as the 'no buttons pressed' condition
    return () => document.removeEventListener('pointermove', listener)
  }, [drag])

  return (
    <DragAndDropContext.Provider
      value={{drag, setDrag, onMoveInternal, onDragStart, onDragEnd}}
    >
      {children}
    </DragAndDropContext.Provider>
  )
}

let dragElementRender
function onDragStartInternal(event, setDrag, onDragStart, ref, id, dragIndex) {
  const {values, dragElementFunc} = onDragStart(id, dragIndex)

  setDrag({id, index: dragIndex, values})

  const dragElementString = dragElementFunc()

  if (dragElementString) {
    dragElementRender = document.createElement('div')
    dragElementRender.className = 'drag-summary-off-screen'
    dragElementRender.innerHTML = dragElementString
    document.body.appendChild(dragElementRender)

    const dragElement = dragElementRender.childNodes[0]

    event.dataTransfer.setDragImage(dragElement, 0, 0)
  } else {
    const box = ref.current.getBoundingClientRect()

    event.dataTransfer.setDragImage(
      ref.current,
      event.clientX - box.x,
      event.clientY - box.y,
    )
  }
}

function onDragEndInternal(event, setDrag, onDragEnd) {
  cleanupDrag(setDrag)

  onDragEnd()
}

function onDropInternal(event, setDrag, onDrop) {
  event.preventDefault()

  cleanupDrag(setDrag)

  if (onDrop) {
    onDrop()
  }
}

function cleanupDrag(setDrag) {
  setDrag(null)

  if (dragElementRender) {
    document.body.removeChild(dragElementRender)
    dragElementRender = null
  }
}

function onDragOver(event, setDrag, drag, hoverID, hoverIndex, onMoveInternal) {
  // onDragStart didn't fire property
  if (!drag) {
    return
  }

  event.preventDefault()

  const {id, index: dragIndex, values} = drag

  // if we are still dragging over the same row
  if (hoverID === id) {
    return
  }

  if (dragIndex < hoverIndex) {
    // moving down (lower in list, to a higher index)
    onMoveInternal(values, hoverID, MOVE_AFTER)
  } else if (dragIndex > hoverIndex) {
    // moving up (higher in list, to a lower index)
    onMoveInternal(values, hoverID, MOVE_BEFORE)
  }

  if (dragIndex !== hoverIndex) {
    // if we got here then we moved
    // the new drag index is the index of the row we hovered over
    setDrag({id, index: hoverIndex, values})
  }
}

/**
 * A drag handle to pull the Draggable element
 *
 * @param {Object} props
 * @param {String} [props.className]
 * @returns {React.ReactNode}
 */
export function DragHandle({className = '', ...props}) {
  const ref = useRef()
  const {setDrag, onDragStart, onDragEnd} = useDragAndDropContext()
  const {id, draggableRef, uiIndex} = useDraggableContext()

  return (
    <div
      ref={ref}
      role="presentation"
      className={`${className} drag-handle`}
      onDragStart={(event) =>
        onDragStartInternal(
          event,
          setDrag,
          onDragStart,
          draggableRef,
          id,
          uiIndex,
        )
      }
      onDragEnd={(event) => onDragEndInternal(event, setDrag, onDragEnd)}
      draggable="true"
      {...props}
    >
      <Icon name="drag-handle" className="i-drag-handle fs-03-04 lh-sm" />
    </div>
  )
}

/**
 * A draggable element to be placed in DragAndDropList
 *
 * @param {Object} props
 * @param {String|Function} props.El
 * @param {String} [props.className]
 * @param {String|Number} props.id
 * @param {Number} props.uiIndex
 * @param {React.ReactNode} props.children
 * @param {Function} [props.onDrop]
 * @returns {React.ReactNode}
 */
export const Draggable = forwardRef(
  (
    {El = 'div', className = '', id, uiIndex, children, onDrop, ...props},
    draggableRef,
  ) => {
    const ref = useRef()
    draggableRef ||= ref
    const {drag, setDrag, onMoveInternal} = useDragAndDropContext()

    const isDraggingPrimary = drag?.index === uiIndex
    const isDraggingOther = !isDraggingPrimary && drag?.values.includes(id)

    return (
      <DraggableContext.Provider value={{draggableRef, id, uiIndex}}>
        <El
          ref={draggableRef}
          className={cn`${className} draggable ${{
            dragging: isDraggingPrimary,
            'dragging-other': isDraggingOther,
          }}`}
          id={id}
          onDragOver={(event) =>
            onDragOver(event, setDrag, drag, id, uiIndex, onMoveInternal)
          }
          onDragEnter={(event) => event.preventDefault()}
          onDrop={(event) => onDropInternal(event, setDrag, onDrop)}
          {...props}
        >
          {children}
        </El>
      </DraggableContext.Provider>
    )
  },
)

Draggable.displayName = 'Draggable'
